/**
 * Dialog zur Erstellung neuer Windpark-Projekte.
 * Nutzt DialogTemplate für einheitliches Layout.
 */
import React, { useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { CloudUpload as CloudUploadIcon } from "@mui/icons-material";

import DialogTemplate from "../../../layout/DialogTemplate";
import Button from "../../../common/inputs/Button";
import InputField from "../../../common/inputs/InputField";
import { MapView, MapBottomBar } from "../../../map";
import { GERMANY_CENTER } from "../../../../utils/geometry";
import UploadDialog from "../../../common/overlays/UploadDialog";
import { Windpark } from "../../../../types";
import { useProjectForm } from "../../../../services/hooks";
import { colors } from "../../../../styles/colors";
import { apiService } from "../../../../services/api";
import { useToast } from "../../../../contexts/ToastContext";

interface NewProjectDialogProps {
  open: boolean;
  onClose: () => void;
  onProjectCreated: (windpark: Windpark) => void;
}

const NewProjectDialog: React.FC<NewProjectDialogProps> = ({
  open,
  onClose,
  onProjectCreated,
}) => {
  const { formData, errors, isSubmitting, handleInputChange, submitForm } =
    useProjectForm();

  const { showSuccess, showError } = useToast();

  const [extractedGeometrie, setExtractedGeometrie] = useState<any | null>(
    null,
  );
  const [isUploadingShapefile, setIsUploadingShapefile] = useState(false);
  const [shapefileDialogOpen, setShapefileDialogOpen] = useState(false);
  const [pendingStandort, setPendingStandort] = useState<
    [number, number] | null
  >(null);

  const handleSave = async () => {
    await submitForm(
      (windpark: Windpark) => {
        onProjectCreated(windpark);
        onClose();
      },
      extractedGeometrie || undefined,
      pendingStandort
        ? { breitengrad: pendingStandort[0], laengengrad: pendingStandort[1] }
        : undefined,
    );
  };

  const handleFileUpload = async (file: File) => {
    setIsUploadingShapefile(true);

    try {
      const result = await apiService.uploadShapefile(file);

      if (result.geometrie) {
        setExtractedGeometrie(result.geometrie);
      } else if (result.flaeche) {
        const coordinates = result.flaeche.map((coord: any) => [
          coord.laengengrad,
          coord.breitengrad,
        ]);
        setExtractedGeometrie({ type: "Polygon", coordinates: [coordinates] });
      }

      setPendingStandort(null);
      showSuccess("Fläche erfolgreich importiert");
    } catch (error: any) {
      showError(error.message || "Fehler beim Verarbeiten des Shapefiles");
    } finally {
      setIsUploadingShapefile(false);
    }
  };

  const handleMapClick = (lat: number, lon: number) => {
    if (!extractedGeometrie) {
      setPendingStandort([lat, lon]);
    }
  };

  return (
    <DialogTemplate
      open={open}
      onClose={onClose}
      title="Neues Projekt"
      maxWidth="sm"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Speichere..." : "Anlegen"}
          </Button>
        </>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 1 }}>
        <InputField
          label="Projektname"
          value={formData.name}
          onChange={(value) => handleInputChange("name", value)}
          placeholder="z.B. Windpark Mandelsloh"
          required={true}
          error={errors.name}
          size="small"
        />

        <Box sx={{ position: "relative" }}>
          <MapView
            projectName=""
            coordinates={
              extractedGeometrie
                ? undefined
                : pendingStandort
                  ? [pendingStandort[0], pendingStandort[1]]
                  : GERMANY_CENTER
            }
            zoom={extractedGeometrie ? undefined : pendingStandort ? 12 : 4}
            geometrie={extractedGeometrie || undefined}
            standortLocation={pendingStandort ?? undefined}
            height="240px"
            showLabel={false}
            showLayerControl={false}
            showMenuButton={false}
            onMapClick={!extractedGeometrie ? handleMapClick : undefined}
          />

          {/* Fläche hochladen — unten Mitte wenn kein Polygon */}
          {!extractedGeometrie && (
            <MapBottomBar>
              <Button
                variant="secondary"
                startIcon={<CloudUploadIcon />}
                onClick={() => setShapefileDialogOpen(true)}
                sx={{ px: 3, py: 1 }}
              >
                Fläche hochladen
              </Button>
            </MapBottomBar>
          )}

          {isUploadingShapefile && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: colors.glass.backgroundSolid,
                zIndex: 1000,
                borderRadius: 1,
              }}
            >
              <Box sx={{ textAlign: "center" }}>
                <CircularProgress size={40} />
                <Typography
                  variant="body2"
                  sx={{ mt: 2, color: "text.secondary" }}
                >
                  Verarbeite Shapefile...
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <UploadDialog
        open={shapefileDialogOpen}
        onClose={() => setShapefileDialogOpen(false)}
        onUpload={handleFileUpload}
        acceptedFileTypes=".zip"
        title="Fläche hochladen"
        dragDropText="Shapefile hierher ziehen"
        acceptedTypesHint="ZIP-Archiv mit Shapefile (.zip)"
      />
    </DialogTemplate>
  );
};

export default NewProjectDialog;
